import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { getUserTimezone } from '../utils/dateUtils.js';
import SignupTierInfo from '../components/SignupTierInfo.jsx';
import SEO from '../components/SEO.jsx';

const SignupPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (formData) => {
    setLoading(true);
    setError('');
    
    // Detect user's timezone
    const timezone = getUserTimezone();
    
    const result = await register(formData.email, formData.password, timezone);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const fields = [
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      placeholder: 'you@example.com',
      required: true
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      placeholder: '••••••••',
      required: true
    }
  ];

  return (
    <>
      <SEO 
        title="Sign Up - IQ Reps"
        description="Create your free IQ Reps account and start your cognitive training journey today."
        noindex={true}
      />
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0f1e 0%, #1e293b 50%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        fontFamily: '"DM Sans", system-ui, sans-serif',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decorative elements */}
        <div style={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(96, 165, 250, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-10%',
          left: '-5%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(167, 139, 250, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          pointerEvents: 'none'
        }} />
        
        <div style={{ 
          width: '100%', 
          maxWidth: '460px',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Main signup card */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1px solid rgba(100, 116, 139, 0.3)',
            padding: '2.5rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(96, 165, 250, 0.1)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h1 style={{
                fontSize: '2.25rem',
                fontWeight: '800',
                marginBottom: '0.5rem',
                background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em'
              }}>
                Create Account
              </h1>
              
              <p style={{
                color: '#94a3b8',
                fontSize: '0.95rem'
              }}>
                Start training your brain today
              </p>
            </div>

            {error && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '10px',
                padding: '1rem',
                marginBottom: '1.5rem',
                color: '#fca5a5',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                {error}
              </div>
            )}

            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = {
                email: e.target.email.value,
                password: e.target.password.value
              };
              handleSubmit(formData);
            }}>
              {fields.map(field => (
                <div key={field.name} style={{ marginBottom: '1.25rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#e2e8f0',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    letterSpacing: '0.01em'
                  }}>
                    {field.label}
                  </label>
                  <input
                    type={field.type || 'text'}
                    name={field.name}
                    required={field.required !== false}
                    placeholder={field.placeholder}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      borderRadius: '10px',
                      border: '1px solid rgba(100, 116, 139, 0.3)',
                      background: 'rgba(15, 23, 42, 0.8)',
                      color: '#e2e8f0',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#60a5fa';
                      e.target.style.background = 'rgba(15, 23, 42, 0.95)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(96, 165, 250, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(100, 116, 139, 0.3)';
                      e.target.style.background = 'rgba(15, 23, 42, 0.8)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              ))}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '1rem',
                  borderRadius: '10px',
                  border: 'none',
                  background: loading 
                    ? 'linear-gradient(135deg, #475569 0%, #334155 100%)' 
                    : 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                  color: '#fff',
                  fontSize: '1.05rem',
                  fontWeight: '700',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: loading ? 0.7 : 1,
                  boxShadow: loading ? 'none' : '0 4px 12px rgba(96, 165, 250, 0.3)',
                  marginTop: '0.5rem',
                  letterSpacing: '0.01em'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(96, 165, 250, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(96, 165, 250, 0.3)';
                  }
                }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <span style={{ 
                      display: 'inline-block',
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite'
                    }} />
                    Creating account...
                  </span>
                ) : (
                  'Create Free Account'
                )}
              </button>
            </form>

            <div style={{
              marginTop: '1.75rem',
              paddingTop: '1.75rem',
              borderTop: '1px solid rgba(100, 116, 139, 0.2)',
              color: '#94a3b8',
              fontSize: '0.9rem',
              textAlign: 'center'
            }}>
              Already have an account?{' '}
              <a href="/login" style={{ 
                color: '#60a5fa', 
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.color = '#93c5fd'}
              onMouseLeave={(e) => e.target.style.color = '#60a5fa'}
              >
                Sign in
              </a>
            </div>
          </div>

          {/* Tier info below */}
          <SignupTierInfo />
        </div>

        {/* Add keyframe animation for loading spinner */}
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </>
  );
};

export default SignupPage;

